// src/pages/LoginPage.js
import React, { useState } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";

const s = {
  page:      { minHeight: "100vh", background: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card:      { background: "#141414", border: "1px solid #272727", borderRadius: 20, padding: 48, width: "100%", maxWidth: 420 },
  logo:      { textAlign: "center", marginBottom: 40 },
  truck:     { fontSize: 48, display: "block", marginBottom: 12 },
  brand:     { fontFamily: "Syne", fontSize: 28, fontWeight: 800, color: "#F97316", letterSpacing: 6 },
  subtitle:  { color: "#666", fontSize: 13, marginTop: 4 },
  label:     { display: "block", color: "#A0A0A0", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 },
  input:     { width: "100%", background: "#1C1C1C", border: "1.5px solid #272727", borderRadius: 10, padding: "12px 16px", color: "#F0F0F0", fontSize: 14, outline: "none", marginBottom: 20, transition: "border-color 0.2s", boxSizing: "border-box" },
  btn:       { width: "100%", background: "#F97316", color: "#fff", border: "none", borderRadius: 10, padding: "14px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "Syne", letterSpacing: 1, transition: "background 0.2s" },
  error:     { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px 14px", color: "#EF4444", fontSize: 13, marginBottom: 20 },
  success:   { background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 8, padding: "12px 14px", color: "#22C55E", fontSize: 13, marginBottom: 20, lineHeight: 1.6 },
  divider:   { borderTop: "1px solid #272727", margin: "28px 0" },
  note:      { color: "#555", fontSize: 12, textAlign: "center", lineHeight: 1.6 },
  forgotLink:{ display: "block", textAlign: "right", color: "#F97316", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: -14, marginBottom: 20, background: "none", border: "none", padding: 0 },
  backLink:  { display: "block", textAlign: "center", color: "#666", fontSize: 13, cursor: "pointer", marginTop: 20, background: "none", border: "none", padding: 0 },
  modeTitle: { color: "#F0F0F0", fontSize: 18, fontWeight: 700, marginBottom: 8 },
  modeDesc:  { color: "#888", fontSize: 13, lineHeight: 1.6, marginBottom: 24 },
};

export default function LoginPage() {
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [mode,      setMode]      = useState("login");
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError("Invalid credentials or you are not an admin.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError("Please enter your admin email address."); return; }
    setLoading(true);
    setError("");
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      // Swallow error intentionally — never reveal if email exists
    } finally {
      setResetSent(true);
      setLoading(false);
    }
  };

  const goToForgot = () => { setMode("forgot"); setError(""); setResetSent(false); };
  const goToLogin  = () => { setMode("login");  setError(""); setResetSent(false); };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          <span style={s.truck}>🚛</span>
          <div style={s.brand}>CREWBOARD</div>
          <div style={s.subtitle}>Admin Control Panel</div>
        </div>

        {mode === "login" && (
          <form onSubmit={handleLogin}>
            {error && <div style={s.error}>{error}</div>}
            <label style={s.label}>Email Address</label>
            <input style={s.input} type="email" value={email} required
              onChange={e => setEmail(e.target.value)} placeholder="admin@crewboard.com"
              onFocus={e => e.target.style.borderColor = "#F97316"}
              onBlur={e  => e.target.style.borderColor = "#272727"} />
            <label style={s.label}>Password</label>
            <input style={s.input} type="password" value={password} required
              onChange={e => setPassword(e.target.value)} placeholder="••••••••"
              onFocus={e => e.target.style.borderColor = "#F97316"}
              onBlur={e  => e.target.style.borderColor = "#272727"} />
            <button type="button" style={s.forgotLink} onClick={goToForgot}>
              Forgot password?
            </button>
            <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign In to Admin Panel"}
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword}>
            <div style={s.modeTitle}>Reset Password</div>
            <div style={s.modeDesc}>
              Enter your admin email and we'll send a reset link if the address is registered.
            </div>
            {error && <div style={s.error}>{error}</div>}
            {resetSent ? (
              <>
                <div style={s.success}>
                  ✅ If that email is registered as an admin, a reset link has been sent.
                  Check your inbox — and your spam folder just in case.
                </div>
                <button type="button" style={s.backLink} onClick={goToLogin}>
                  ← Back to Sign In
                </button>
              </>
            ) : (
              <>
                <label style={s.label}>Admin Email Address</label>
                <input style={s.input} type="email" value={email} required
                  onChange={e => setEmail(e.target.value)} placeholder="admin@crewboard.com"
                  onFocus={e => e.target.style.borderColor = "#F97316"}
                  onBlur={e  => e.target.style.borderColor = "#272727"} />
                <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} type="submit" disabled={loading}>
                  {loading ? "Sending…" : "Send Reset Link"}
                </button>
                <button type="button" style={s.backLink} onClick={goToLogin}>
                  ← Back to Sign In
                </button>
              </>
            )}
          </form>
        )}

        <div style={s.divider} />
        <div style={s.note}>
          🔒 This panel is restricted to authorized Crewboard admins only.<br />
          Unauthorized access attempts are logged.
        </div>
      </div>
    </div>
  );
}